/**
 * @file button-distribute.tsx
 *
 * MD3 Expressive ButtonDistribute component.
 * Flexible Button Group with Expressive Motion & Size Distribution (Fixed, Dynamic, Mixed).
 */

import { domMax, LazyMotion, m } from "motion/react";
import * as React from "react";
import { cn } from "../../../lib/utils";
import { BUTTON_RADIUS_SPRING } from "../button/button-tokens";
import {
	DISTRIBUTE_HEIGHT_TOKENS,
	DISTRIBUTE_RADIUS_TOKENS,
	DISTRIBUTE_SPRING_TRANSITION,
} from "./button-distribute-tokens";
import type { ButtonDistributeProps } from "./types";
import { useDistributeWeights } from "./use-distribute-weights";

export const ButtonDistribute = React.forwardRef<
	HTMLFieldSetElement,
	ButtonDistributeProps
>(
	(
		{
			className,
			style,
			mode = "dynamic",
			weights,
			expandRatio = 0.3,
			expandOnHover = false,
			fixedSize = 48,
			leadingSize,
			trailingSize,
			gap = "0.5rem",
			size = "sm",
			itemClassName,
			children,
			onMouseLeave,
			...restProps
		},
		ref,
	) => {
		const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
		const [pressedIndex, setPressedIndex] = React.useState<number | null>(null);

		const childrenArray = React.useMemo(
			() => React.Children.toArray(children).filter(React.isValidElement),
			[children],
		);
		const count = childrenArray.length;

		const isTextItem = React.useMemo(() => {
			return childrenArray.map((child) => {
				const childEl = child as React.ReactElement<{
					children?: React.ReactNode;
				}>;
				if (typeof childEl.props.children === "string") return true;
				if (Array.isArray(childEl.props.children)) {
					return childEl.props.children.some(
						(c) => typeof c === "string" && c.trim().length > 0,
					);
				}
				return false;
			});
		}, [childrenArray]);

		const calculatedWeights = useDistributeWeights({
			count,
			weights,
			activeIndex,
			expandRatio,
			mode,
			isTextItem,
		});

		const handleGroupMouseLeave = React.useCallback(
			(e: React.MouseEvent<HTMLFieldSetElement>) => {
				setActiveIndex(null);
				setPressedIndex(null);
				onMouseLeave?.(e);
			},
			[onMouseLeave],
		);

		const resolvedGap = typeof gap === "number" ? `${gap}px` : gap;
		const effectiveLeadingSize =
			typeof (leadingSize ?? fixedSize) === "number"
				? `${leadingSize ?? fixedSize}px`
				: (leadingSize ?? fixedSize);
		const effectiveTrailingSize =
			typeof (trailingSize ?? fixedSize) === "number"
				? `${trailingSize ?? fixedSize}px`
				: (trailingSize ?? fixedSize);

		const radiusTokens =
			DISTRIBUTE_RADIUS_TOKENS[size] ?? DISTRIBUTE_RADIUS_TOKENS.sm;
		const heightClass =
			DISTRIBUTE_HEIGHT_TOKENS[size] ?? DISTRIBUTE_HEIGHT_TOKENS.sm;

		return (
			<LazyMotion features={domMax} strict>
				<fieldset
					ref={ref}
					onMouseLeave={handleGroupMouseLeave}
					onPointerCancel={() => setPressedIndex(null)}
					className={cn(
						"inline-flex flex-row items-center border-none p-0 m-0 box-border overflow-hidden select-none w-full",
						heightClass,
						className,
					)}
					style={{
						gap: resolvedGap,
						...style,
					}}
					{...restProps}
				>
					{childrenArray.map((child, index) => {
						const isFirst = index === 0;
						const isLast = index === count - 1;
						const childElement = child as React.ReactElement<{
							size?: string;
							className?: string;
							style?: React.CSSProperties;
							onMouseEnter?: React.MouseEventHandler;
							onFocus?: React.FocusEventHandler;
							onPointerDown?: React.PointerEventHandler;
							onPointerUp?: React.PointerEventHandler;
							onClick?: React.MouseEventHandler;
						}>;

						const isActive = activeIndex === index;
						const isPressed = pressedIndex === index;
						const isSquish = isActive || isPressed;

						let flexStyle: React.CSSProperties = {};
						let targetFlexBasis: string | number = 0;

						const parsePx = (val: string | number): number => {
							if (typeof val === "number") return val;
							const parsed = Number.parseFloat(val);
							return Number.isNaN(parsed) ? 48 : parsed;
						};

						const leadingPx = parsePx(effectiveLeadingSize);
						const trailingPx = parsePx(effectiveTrailingSize);
						const leadingCompressedPx = Math.max(
							40,
							Math.round(leadingPx * (1 - expandRatio)),
						);
						const trailingCompressedPx = Math.max(
							40,
							Math.round(trailingPx * (1 - expandRatio)),
						);

						if (mode === "dynamic" || mode === "fixed") {
							const currentWeight = calculatedWeights[index] ?? 1;
							flexStyle = {
								flexGrow: currentWeight,
								flexShrink: 1,
								flexBasis: 0,
								minWidth: 0,
							};
							targetFlexBasis = 0;
						} else if (mode === "mixed") {
							const currentWeight = calculatedWeights[index] ?? 0;
							if (isFirst) {
								targetFlexBasis =
									activeIndex !== null && activeIndex > 0
										? `${leadingCompressedPx}px`
										: effectiveLeadingSize;
								flexStyle = {
									flexBasis: targetFlexBasis,
									flexGrow: currentWeight,
									flexShrink: 0,
									minWidth: 0,
								};
							} else if (isLast) {
								targetFlexBasis =
									activeIndex !== null && activeIndex < count - 1
										? `${trailingCompressedPx}px`
										: effectiveTrailingSize;
								flexStyle = {
									flexBasis: targetFlexBasis,
									flexGrow: currentWeight,
									flexShrink: 0,
									minWidth: 0,
								};
							} else {
								targetFlexBasis = 0;
								flexStyle = {
									flexBasis: 0,
									flexGrow: calculatedWeights[index] ?? 1,
									flexShrink: 1,
									minWidth: 0,
								};
							}
						}

						const handleMouseEnter = (e: React.MouseEvent) => {
							if (expandOnHover) {
								setActiveIndex(index);
							}
							childElement.props.onMouseEnter?.(e);
						};

						const handleFocus = (e: React.FocusEvent) => {
							setActiveIndex(index);
							childElement.props.onFocus?.(e);
						};

						const handlePointerDown = (e: React.PointerEvent) => {
							setPressedIndex(index);
							setActiveIndex(index);
							childElement.props.onPointerDown?.(e);
						};

						const handlePointerUp = (e: React.PointerEvent) => {
							setPressedIndex(null);
							if (!expandOnHover) {
								setActiveIndex(null);
							}
							childElement.props.onPointerUp?.(e);
						};

						const handleClick = (e: React.MouseEvent) => {
							setPressedIndex(null);
							if (!expandOnHover) {
								setActiveIndex(null);
							}
							childElement.props.onClick?.(e);
						};

						const clonedChild = React.cloneElement(childElement, {
							size: childElement.props.size ?? size,
							className: cn(
								"w-full! h-full! justify-center items-center flex-1 rounded-[inherit]! px-3! sm:px-4!",
								childElement.props.className,
							),
						});

						return (
							<m.div
								key={childElement.key ?? index}
								layout
								initial={false}
								animate={{
									flexGrow: (flexStyle.flexGrow as number) ?? 1,
									flexBasis: targetFlexBasis,
									borderRadius: isSquish
										? radiusTokens.pressedRadius
										: radiusTokens.pillRadius,
								}}
								transition={{
									...DISTRIBUTE_SPRING_TRANSITION,
									borderRadius: BUTTON_RADIUS_SPRING,
								}}
								onMouseEnter={handleMouseEnter}
								onFocus={handleFocus}
								onPointerDown={handlePointerDown}
								onPointerUp={handlePointerUp}
								onClick={handleClick}
								className={cn(
									"relative inline-flex items-center justify-center shrink-0 min-w-0 overflow-hidden h-full",
									itemClassName,
								)}
								style={flexStyle}
							>
								{clonedChild}
							</m.div>
						);
					})}
				</fieldset>
			</LazyMotion>
		);
	},
);

ButtonDistribute.displayName = "ButtonDistribute";
